import { BookingServiceClient } from 'fitbeat.api-clients';
import { HomeWorkoutSequence, UserProfile } from 'fitbeat.gym-core';
import { musicPlatform } from 'fitbeat.gym-core/config';
import React, { FunctionComponent } from 'react';
import { translate } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { connect } from 'react-redux';
import HRMonitorIcon from '../../../assets/images/home-workouts/heart-rate.svg';
import LeaderboardIcon from '../../../assets/images/home-workouts-thin/leaderboard.svg';
import MirrorIcon from '../../../assets/images/home-workouts-thin/mirror.svg';
import MusicIcon from '../../../assets/images/home-workouts-thin/music-note-double.svg';
import ProfileIcon from '../../../assets/images/home-workouts-thin/profile.svg';
import { shareInvite } from '../../../Bookings';
import Theme from '../../../theme';
import { TileSection } from '../../../ui';
import { defaultScale } from '../../../utils/scaling';
import { inviteFriendsPressed, openSetupScreen } from '../../actions';
import {
  getHRMonitorConfiguredStateSelector,
  hasSetupHomeHeartRateMonitorSelector,
  HRMonitorConfiguredState,
  liveHeartRateSelector, liveHeartRateSourceTypeSelector, mirrorSetupSelector,
  shouldDisableHeartRateMonitorEditSelector,
  shouldDisableMusicEditSelector,
} from '../../selectors';
import { DeviceSetupScreens } from '../../utils/DeviceSetupScreens';
import { didMusicPlayerToAutoplayFailSelector,
  getMusicPlayerToAutoplaySelector, getSelectedMusicPlayerSelector, hasSetupMusicSelector } from '../Music';
import { BoostIcon } from './BoostIcon';
import { IAppStore } from '../../reducer';

interface IProps {
  leaderboardActive: boolean;
  shareInvitePressed: () => void;
  openDeviceSetupScreen: (screen: DeviceSetupScreens) => void;
  t: (key: string, options?: any) => string;
  bookingServiceClient: BookingServiceClient;
  userProfile: UserProfile;
  homeWorkoutSession: HomeWorkoutSequence;
  loadingShareLink: boolean;
  invitePressed: () => void;
  isGroupWorkout: boolean;
  liveHeartRate: number | null;
  liveHeartRateSourceType: 'ConnectedApp' | 'Bluetooth' | null;
  hasSetupMusic: boolean;
  hasSetupHeartRateMonitor: boolean;
  participantCount: number;
  hrMonitorConfiguredState: HRMonitorConfiguredState;
  mirrorIsSetup: boolean;
  selectedMusicPlayer: musicPlatform | null;
  musicPlayerToAutoplay: musicPlatform | null;
  didMusicPlayerToAutoplayFail: boolean;
  disableMusicButton: boolean;
  disableHeartRateButton: boolean;
  disableOtherButtons: boolean;
  waitingForWatchHRData: boolean;
  failedToReceiveHRFromConnectedApp: boolean;
  failedToReceiveHRFromConnectedAppError: 'Timed out' | string | null;
}

export const BoostYourExperienceComponent: FunctionComponent<IProps> = ({
                                                                          openDeviceSetupScreen,
                                                                          t,
                                                                          invitePressed,
                                                                          loadingShareLink,
                                                                          isGroupWorkout,
                                                                          liveHeartRate,
                                                                          liveHeartRateSourceType,
                                                                          hasSetupMusic,
                                                                          hasSetupHeartRateMonitor,
                                                                          participantCount,
                                                                          hrMonitorConfiguredState,
                                                                          mirrorIsSetup,
                                                                          selectedMusicPlayer,
                                                                          musicPlayerToAutoplay,
                                                                          didMusicPlayerToAutoplayFail,
                                                                          leaderboardActive,
                                                                          disableMusicButton,
                                                                          disableHeartRateButton,
                                                                          disableOtherButtons,
                                                                          waitingForWatchHRData,
                                                                          failedToReceiveHRFromConnectedApp,
                                                                          failedToReceiveHRFromConnectedAppError,
                                                                        }) => {

  const ICON_SIZE = defaultScale(30);
  const HR_ICON_SIZE = defaultScale(32);
  const svgSize = {
    height: ICON_SIZE,
    width: ICON_SIZE,
  };
  const hrSvgSize = {
    height: HR_ICON_SIZE,
    width: HR_ICON_SIZE,
  };

  const friendsJoining = (): string => {
    if (participantCount === 2) {
      return t('oneFriend');
    } else if (participantCount > 2) {
      return t('countFriends', { count: participantCount - 1 });
    }
    return '';
  };

  const usingConnectedAppsForHR = liveHeartRateSourceType === 'ConnectedApp';
  const haveHRData = (liveHeartRate ?? 0) > 0;

  const hasHRDevice = hasSetupHeartRateMonitor || usingConnectedAppsForHR || waitingForWatchHRData;

  const isHRMonitorLive = hrMonitorConfiguredState === HRMonitorConfiguredState.live;
  const isUsingLiveHRData = haveHRData && (isHRMonitorLive || usingConnectedAppsForHR);

  const isHRSetup = hasHRDevice && !failedToReceiveHRFromConnectedApp;

  const getHeartRateTextLine2 = () => {
    let connectingDisplayStatus = t('scanning');
    if (waitingForWatchHRData) {
      connectingDisplayStatus = t('activating');
    } else if (failedToReceiveHRFromConnectedApp) {
      if (failedToReceiveHRFromConnectedAppError === 'Timed out') {
        connectingDisplayStatus = t('timeout');
      } else {
        connectingDisplayStatus = t('notconnected');
      }
    }

    return {
      isSetup: isUsingLiveHRData
        ? t('liveBPM', { bpm: liveHeartRate })
        : connectingDisplayStatus,
      notSetup: hasHRDevice ? connectingDisplayStatus : t('monitor')
    };
  };

  const heartRateButton = <BoostIcon isSetup={isHRSetup}
                                     textLine1={{ isSetup: t('heartRate'), notSetup: t('heartRate') }}
                                     textLine2={getHeartRateTextLine2()}
                                     disabled={disableHeartRateButton}
                                     icon={(color) => <HRMonitorIcon color={color} {...hrSvgSize} />}
                                     onPress={() => openDeviceSetupScreen(DeviceSetupScreens.HeartRate)} />;

  const inviteFriendsButton = <BoostIcon isSetup={participantCount > 1}
                                         textLine1={{ isSetup: friendsJoining(), notSetup: t('invite') }}
                                         textLine2={{ isSetup: t('joining'), notSetup: t('friends') }}
                                         loading={loadingShareLink}
                                         disabled={disableOtherButtons}
                                         icon={(color) => <ProfileIcon color={color} {...svgSize} />}
                                         onPress={invitePressed} />;

  const leaderBoardButton = <BoostIcon isSetup={leaderboardActive}
                                       textLine1={{ isSetup: t('leaderboard'), notSetup: t('enable') }}
                                       textLine2={{ isSetup: t('on'), notSetup: t('leaderboard').toLocaleLowerCase() }}
                                       disabled={disableOtherButtons}
                                       icon={(color) => <LeaderboardIcon color={color} {...svgSize} />}
                                       onPress={() => openDeviceSetupScreen(DeviceSetupScreens.LeaderboardSetup)} />;

  const musicButton = <BoostIcon isSetup={hasSetupMusic}
                                 textLine1={{
                                  isSetup: selectedMusicPlayer || '',
                                  notSetup:
                                  musicPlayerToAutoplay && disableMusicButton ? musicPlayerToAutoplay
                                  : musicPlayerToAutoplay && didMusicPlayerToAutoplayFail ? musicPlayerToAutoplay
                                  : t('bookings:customise'),
                                  }}
                                 textLine2={{
                                  isSetup: t('playing'),
                                  notSetup:
                                    musicPlayerToAutoplay && disableMusicButton ? t('loading')
                                    : musicPlayerToAutoplay && didMusicPlayerToAutoplayFail ? t('disconnected')
                                    : t('music').toLocaleLowerCase(),
                                }}
                                 disabled={disableMusicButton}
                                 icon={(color) => <MusicIcon color={color} {...svgSize} />}
                                 onPress={() => openDeviceSetupScreen(DeviceSetupScreens.Music)} />;

  const mirrorButton = <BoostIcon isSetup={mirrorIsSetup} textLine1={{ isSetup: t('exercise'), notSetup: t('exercise') }}
                                  textLine2={{ isSetup: t('mirrorOn'), notSetup: t('mirror') }}
                                  disabled={disableOtherButtons}
                                  icon={(color) => <MirrorIcon color={color} {...svgSize} />}
                                  onPress={() => openDeviceSetupScreen(DeviceSetupScreens.ScreenMirror)} />;

  return (
    <TileSection heading={t('boostYourExperience')}>
      <View style={styles.buttonContainer}>
        {
          isGroupWorkout &&
          <>
            {heartRateButton}
            {inviteFriendsButton}
            {leaderBoardButton}
          </>
        }
        {
          !isGroupWorkout &&
          <>
            {musicButton}
            {heartRateButton}
            {mirrorButton}
            {leaderBoardButton}
          </>
        }
      </View>
    </TileSection>
  );
};

const mapStateToProps = (state: IAppStore): Partial<IProps> => {
  const { openInviteClassesByWorkoutId } = state.bookings.bookings;
  const openInviteClass = openInviteClassesByWorkoutId[state.homeWorkouts.homeWorkoutId];
  const participantCount = openInviteClass ? openInviteClass.openInviteUserProfiles.length : 1;

  return {
    loadingShareLink: state.homeWorkouts.loadingShareLink,
    waitingForWatchHRData: state.heartRateMonitor.waitingForWatchHRData,
    failedToReceiveHRFromConnectedApp: state.heartRateMonitor.failedToReceiveHRFromConnectedApp,
    failedToReceiveHRFromConnectedAppError: state.heartRateMonitor.failedToReceiveHRFromConnectedAppError,
    isGroupWorkout: state.homeWorkouts.isGroupWorkout,
    liveHeartRate: liveHeartRateSelector(state),
    liveHeartRateSourceType: liveHeartRateSourceTypeSelector(state),
    hasSetupMusic: hasSetupMusicSelector(state),
    hasSetupHeartRateMonitor: hasSetupHomeHeartRateMonitorSelector(state),
    selectedMusicPlayer: getSelectedMusicPlayerSelector(state),
    musicPlayerToAutoplay: getMusicPlayerToAutoplaySelector(state),
    didMusicPlayerToAutoplayFail: didMusicPlayerToAutoplayFailSelector(state),
    participantCount,
    hrMonitorConfiguredState: getHRMonitorConfiguredStateSelector(state),
    mirrorIsSetup: mirrorSetupSelector(state),
    leaderboardActive: state.homeWorkouts.isLeaderboardActivated,
    disableOtherButtons: state.homeWorkouts.homeWorkoutLoading,
    disableHeartRateButton: shouldDisableHeartRateMonitorEditSelector(state),
    disableMusicButton: shouldDisableMusicEditSelector(state),
  };
};

const mapDispatchToProps = (dispatch, ownProps: IProps): Partial<IProps> => {
  const { bookingServiceClient, userProfile, t } = ownProps;
  return {
    invitePressed: () => dispatch(inviteFriendsPressed(bookingServiceClient, userProfile, t)),
    openDeviceSetupScreen: (screen: DeviceSetupScreens) => dispatch(openSetupScreen(screen)),
    shareInvitePressed: () => dispatch(shareInvite()),
  };
};

export const BoostYourExperience =
  translate(['homeWorkouts', 'dashboard'])(connect(mapStateToProps, mapDispatchToProps)(BoostYourExperienceComponent));

const styles = StyleSheet.create({
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
});
